import { makeAutoObservable } from "mobx";
import { ExtendedShareholderDTO } from "@/types/Shareholder";
import { groupBy, parseInt, sortBy, uniqBy } from "lodash";
import { newUuid } from "@/utils/uuid";
import { ShareholdingAllocation, ShareholdingPerDateDTO, Transaction } from "@/types/Transactions";
import { combineDateAndTime, toDate } from "@/utils/Dates";
import { isBlank } from "@/utils/strings";

import { TMessage } from "@/i18n/tt";
import { LocalDateFieldModel, NumberFieldModel, StringFieldModel } from "@/common/commonForms";
import {
  calculateTotalNumberOfShares,
  SharenumberInterval,
} from "@/shareholderRegister/shareholderRegisterDomain";
import { ShareholderIdShares } from "@/types/excelImportDomain";
import { isNorwegianOrgNo } from "@/utils/orgNo";
import { ShareType } from "@/common/share";
import {
  excludedShareholdersFromActors,
  excludeExistingShareholders,
} from "@/stores/ShareholdersStore";
import {
  dateBefore,
  TransactionDateCardModel,
} from "@/shareholderRegister/transactions/commonTransactionsDomain";
import { TransactionStore } from "@/stores/TransactionStore";

export class SharesPerShareTypeModel {
  shareType: ShareType;
  numberOfShares: NumberFieldModel;

  constructor(shareType: ShareType, setDirty: () => void, numberOfShares?: number) {
    this.shareType = shareType;
    this.numberOfShares = new NumberFieldModel({
      onChange: setDirty,
      value: numberOfShares,
      isPositiveOrZero: true,
    });
  }
}

function splitSharenumbers(value: string) {
  return value
    .split(",")
    .filter((s) => !isBlank(s))
    .map((s) => s.trim());
}

export function validateSharenumberIntervals(value: string): TMessage | undefined {
  if (isBlank(value)) {
    return undefined;
  }
  const sharenumberIntervals = splitSharenumbers(value);
  if (sharenumberIntervals.some((s) => !/^\d+-\d+$/.test(s) && !/^\d+$/.test(s))) {
    return { key: "transactions.shareholdingPerDate.sharenumberIntervals.invalid" };
  }
  return undefined;
}

export function parseSharenumbers(input: string): SharenumberInterval[] {
  return splitSharenumbers(input).map((s) => {
    const [from, to] = s.includes("-") ? s.split("-") : [s, s];
    return {
      sharenumberFromInclusive: parseInt(from),
      sharenumberToInclusive: parseInt(to),
    };
  });
}

function formatSharenumberIntervals(intervals: SharenumberInterval[]): string {
  return intervals
    .map((i) => `${i.sharenumberFromInclusive}-${i.sharenumberToInclusive}`)
    .join(", ");
}

export class SharesPerShareholderModel {
  id = newUuid();
  shareType: ShareType;
  shareholder: ExtendedShareholderDTO;
  entryDate: LocalDateFieldModel;
  numberOfShares: NumberFieldModel;
  sharenumberIntervals: StringFieldModel;
  sharePrice: NumberFieldModel;
  sharePremium: NumberFieldModel;
  paidInShareCapital: NumberFieldModel;
  bonusShareCapital: NumberFieldModel;

  constructor(
    shareType: ShareType,
    shareholder: ExtendedShareholderDTO,
    setDirty: () => void,
    numberOfShares?: number,
    shareNumbers?: string,
    entryDate?: string,
    sharePrice?: number,
    sharePremium?: number,
    paidInShareCapital?: number,
    bonusShareCapital?: number,
  ) {
    this.shareType = shareType;
    this.shareholder = shareholder;
    this.entryDate = new LocalDateFieldModel({ onChange: setDirty, value: entryDate });
    this.numberOfShares = new NumberFieldModel({
      onChange: setDirty,
      value: numberOfShares,
      isPositiveOrZero: true,
    });
    this.sharenumberIntervals = new StringFieldModel({
      onChange: setDirty,
      validator: (value) => this.validateSharenumbers(value),
      value: shareNumbers,
    });
    this.sharePrice = new NumberFieldModel({
      onChange: setDirty,
      value: sharePrice,
      isPositiveOrZero: true,
    });
    this.sharePremium = new NumberFieldModel({
      onChange: setDirty,
      value: sharePremium,
      isPositiveOrZero: true,
    });
    this.paidInShareCapital = new NumberFieldModel({
      onChange: setDirty,
      value: paidInShareCapital,
      isPositiveOrZero: true,
    });
    this.bonusShareCapital = new NumberFieldModel({
      onChange: setDirty,
      value: bonusShareCapital,
      isPositiveOrZero: true,
    });
    makeAutoObservable(this, {}, { autoBind: true });
  }

  private validateSharenumbers(value: string): TMessage | undefined {
    const error = validateSharenumberIntervals(value);
    if (error) {
      return error;
    }
    const sharenumberIntervals = parseSharenumbers(value);
    const numberOfShares = this.numberOfShares.toNumberOrZero();
    const sumOfIntervals = calculateTotalNumberOfShares(sharenumberIntervals);
    if (sumOfIntervals !== numberOfShares) {
      return { key: "transactions.shareholdingPerDate.sharenumberIntervals.sumMismatch" };
    }
  }
}

function createSharesPerShareholderModel(
  shareType: ShareType,
  shareholder: ExtendedShareholderDTO,
  setDirty: () => void,
  shareholderIdShares?: ShareholderIdShares[],
  entryDateByShareholderId?: Map<number, string | undefined>,
) {
  const shareholderData = shareholderIdShares?.find((s) => s.shareholderId === shareholder.id);
  const shares = shareholderData?.shares?.find((s) => s.shareTypeId === shareType.sharetypeId);
  return new SharesPerShareholderModel(
    shareType,
    shareholder,
    setDirty,
    shares?.numberOfShares,
    shares?.shareNumbers,
    entryDateByShareholderId?.get(shareholder.id),
    shares?.sharePrice,
    shares?.sharePremium,
    shares?.paidInShareCapital,
    shares?.bonusShareCapital,
  );
}

function createSharetypeModel(
  shareType: ShareType,
  sharesPerShareHolders: SharesPerShareholderModel[],
  setDirty: () => void,
) {
  const numberOfShares = sharesPerShareHolders
    .filter((s) => s.shareType.sharetypeId === shareType.sharetypeId)
    .map((s) => s.numberOfShares.toNumberOrZero())
    .reduce((x, y) => x + y, 0);
  return new SharesPerShareTypeModel(shareType, setDirty, numberOfShares);
}

function buildShareholderIdShares(
  shareAllocation: ShareholdingAllocation[],
): ShareholderIdShares[] {
  return Object.entries(groupBy(shareAllocation, "shareholderId")).map(
    ([shareholderId, allocations]) => ({
      shareholderId: Number(shareholderId),
      shares: allocations.map((a) => ({
        shareTypeId: a.sharetypeId,
        numberOfShares: a.numberOfShares,
        shareNumbers:
          a.sharenumberIntervals && a.sharenumberIntervals.length > 0
            ? formatSharenumberIntervals(a.sharenumberIntervals)
            : undefined,
        sharePrice: a.sharePrice,
        sharePremium: a.sharePremium,
        paidInShareCapital: a.paidInShareCapital,
        bonusShareCapital: a.bonusShareCapital,
      })),
    }),
  );
}

function buildEntryDateByShareholderId(
  shareAllocation: ShareholdingAllocation[],
): Map<number, string | undefined> {
  return new Map(shareAllocation.map((a) => [a.shareholderId, a.entryDate?.split("T")[0]]));
}

export class ShareholdingPerDateModel {
  transactionDateCardModel: TransactionDateCardModel;
  nominalValue: NumberFieldModel;
  sharesPerShareTypes: SharesPerShareTypeModel[] = [];
  sharesPerShareHolders: SharesPerShareholderModel[] = [];
  error?: TMessage;
  isDirty = false;
  shareholdingPerDateUnsupportedForARError?: TMessage;
  enterShareNumbers = false;
  enterTaxPositions = false;

  constructor(
    readonly transactionStore: TransactionStore,
    readonly companyData: { orgNumber: string },
    readonly shareholderData: {
      activeShareTypes: ShareType[];
      shareTypesWithShares: ShareType[];
      shareholders: ExtendedShareholderDTO[];
      transactionLog: Transaction[];
      loadShareholdersForDate: (orgNumber: string, date: string) => unknown;
    },
    readonly transactionData?: ShareholdingPerDateDTO,
  ) {
    this.transactionDateCardModel = new TransactionDateCardModel({
      data: shareholderData,
      value: transactionData?.transactionDate,
      onChange: (date, time) => {
        this.setDirty();
        if (date && time) {
          this.checkReportingYearFor(date);
          this.fetchState(combineDateAndTime(date, time));
        }
      },
    });
    this.nominalValue = new NumberFieldModel({
      required: true,
      onChange: this.setDirty,
      isPositiveOrZero: true,
      value: transactionData?.nominalValue,
    });
    makeAutoObservable(
      this,
      { transactionStore: false, companyData: false, shareholderData: false },
      { autoBind: true },
    );
  }

  *fetchState(date: string) {
    yield this.shareholderData.loadShareholdersForDate(
      this.companyData.orgNumber,
      dateBefore(date),
    );
    const shareholderIdShares = this.transactionData
      ? buildShareholderIdShares(this.transactionData.shareAllocation)
      : undefined;
    const entryDateByShareholderId = this.transactionData
      ? buildEntryDateByShareholderId(this.transactionData.shareAllocation)
      : undefined;
    this.initShareRows(shareholderIdShares, entryDateByShareholderId);
  }

  initFromImport(
    shareholderIdShares: ShareholderIdShares[],
    entryDateByShareholderId: Map<number, string | undefined>,
  ) {
    this.transactionDateCardModel = new TransactionDateCardModel({
      data: this.shareholderData,
    });

    this.initShareRows(shareholderIdShares, entryDateByShareholderId);
  }

  private initShareRows(
    shareholderIdShares?: ShareholderIdShares[],
    entryDateByShareholderId?: Map<number, string | undefined>,
  ) {
    const shareTypes = this.shareholderData.activeShareTypes;
    const shareholders = this.shareholderData.shareholders;
    this.sharesPerShareHolders = sortBy(shareholders, "name").flatMap((shareholder) =>
      shareTypes.map((shareType) =>
        createSharesPerShareholderModel(
          shareType,
          shareholder,
          this.setDirty,
          shareholderIdShares,
          entryDateByShareholderId,
        ),
      ),
    );
    this.enterShareNumbers =
      shareholderIdShares?.some((s) => s.shares?.some((ss) => ss.shareNumbers)) ?? false;
    this.enterTaxPositions =
      shareholderIdShares?.some((s) =>
        s.shares?.some(
          (ss) =>
            ss.sharePrice !== undefined ||
            ss.sharePremium !== undefined ||
            ss.paidInShareCapital !== undefined ||
            ss.bonusShareCapital !== undefined,
        ),
      ) ?? false;
    this.sharesPerShareTypes = shareTypes.map((shareType) =>
      createSharetypeModel(shareType, this.sharesPerShareHolders, this.setDirty),
    );
  }

  get shareTypes(): ShareType[] {
    return this.shareholderData.activeShareTypes;
  }

  get orgNo(): string {
    return this.companyData.orgNumber;
  }

  private get formShareholders(): ExtendedShareholderDTO[] {
    return uniqBy(this.sharesPerShareHolders, (s) => s.shareholder.id).map((s) => s.shareholder);
  }

  *onNewShareTypeAdded(shareType: ShareType) {
    const date = this.transactionDateCardModel.combinedDateAndTime;
    if (this.formShareholders.length === 0 && date) {
      yield this.fetchState(date);
    } else {
      this.sharesPerShareTypes.push(
        createSharetypeModel(shareType, this.sharesPerShareHolders, this.setDirty),
      );
      this.formShareholders.forEach((shareholder) => {
        this.sharesPerShareHolders.push(
          new SharesPerShareholderModel(shareType, shareholder, this.setDirty),
        );
      });
      this.sharesPerShareHolders = sortBy(this.sharesPerShareHolders, (s) => s.shareholder.id);
    }
  }

  addNewShareholder(shareholder: ExtendedShareholderDTO) {
    this.sharesPerShareHolders = [
      ...this.sharesPerShareHolders,
      ...this.sharesPerShareTypes.map(
        (st) => new SharesPerShareholderModel(st.shareType, shareholder, this.setDirty),
      ),
    ];
  }

  setDirty() {
    this.isDirty = true;
  }

  get numberOfShareholders() {
    return this.sharesPerShareHolders.length / this.shareTypes.length;
  }

  get hasShareholders(): boolean {
    return this.sharesPerShareHolders.length > 0;
  }

  get activeShareTypeIds() {
    const shareTypesWithValues = this.sharesPerShareTypes
      .filter((s) => !s.numberOfShares.isEmpty)
      .map((s) => s.shareType.sharetypeId);
    return shareTypesWithValues.length > 0
      ? shareTypesWithValues
      : this.sharesPerShareTypes.slice(0, 1).map((s) => s.shareType.sharetypeId);
  }

  get isValid(): boolean {
    return !this.error && this.transactionDateCardModel.isValid && this.nominalValue.isValid;
  }

  validate(): boolean {
    this.error = undefined;
    this.transactionDateCardModel.validate();
    this.nominalValue.validate();

    this.sharesPerShareTypes.forEach((s) => s.numberOfShares.validate());
    this.sharesPerShareHolders.forEach((s) => s.numberOfShares.validate());
    if (this.sharesPerShareHolders.some((s) => !s.entryDate.validate())) {
      this.error = { key: "transactions.shareholdingPerDate.invalidEntryDate" };
    }

    if (!this.sharesPerShareHolders.some((s) => s.numberOfShares.toNumberOrZero() > 0)) {
      this.error = { key: "transactions.shareholdingPerDate.missingSharesPerShareholderError" };
    }
    if (!this.sharesPerShareTypes.some((s) => s.numberOfShares.toNumberOrZero() > 0)) {
      this.error = { key: "transactions.shareholdingPerDate.missingSharesPerShareTypeError" };
    }
    if (!this.error) {
      if (this.hasShareSumMismatch()) {
        this.error = { key: "transactions.shareholdingPerDate.shareSumError" };
      }
    }
    if (!this.error && this.enterShareNumbers) {
      if (this.isMissingSharenumbers()) {
        this.error = { key: "transactions.shareholdingPerDate.sharenumberIntervals.missing" };
      } else if (this.sharesPerShareHolders.some((s) => !s.sharenumberIntervals.validate())) {
        this.error = { key: "transactions.shareholdingPerDate.sharenumberIntervals.sumMismatch" };
      }
    }

    return this.isValid;
  }

  hasShareSumMismatch() {
    return this.sharesPerShareTypes
      .filter((s) => !s.numberOfShares.isEmpty)
      .some(
        (sharesPerShareType) =>
          sharesPerShareType.numberOfShares.toNumberOrZero() !==
          this.getAllocatedShares(sharesPerShareType),
      );
  }

  isMissingSharenumbers() {
    return this.sharesPerShareHolders.some(
      (s) => s.numberOfShares.toNumberOrZero() > 0 && s.sharenumberIntervals.isEmpty,
    );
  }

  private getAllocatedShares(sharesPerShareType: SharesPerShareTypeModel) {
    return this.sharesPerShareHolders
      .filter((s) => s.shareType.sharetypeId === sharesPerShareType.shareType.sharetypeId)
      .map((s) => s.numberOfShares.toNumberOrZero())
      .reduce((previous, current) => previous + current);
  }

  prepareData(): ShareholdingPerDateDTO {
    return {
      organizationnumber: this.orgNo,
      shareAllocation: this.sharesPerShareHolders
        .map((s) => ({
          shareholderId: s.shareholder.id,
          sharetypeId: s.shareType.sharetypeId,
          numberOfShares: s.numberOfShares.parsedValue ?? 0,
          sharenumberIntervals: this.enterShareNumbers
            ? parseSharenumbers(s.sharenumberIntervals.validatedValue)
            : undefined,
          entryDate: s.entryDate.validatedValue
            ? combineDateAndTime(s.entryDate.validatedValue, "12:00")
            : undefined,
          ...(this.enterTaxPositions && {
            sharePrice: s.sharePrice.parsedValue,
            sharePremium: s.sharePremium.parsedValue,
            paidInShareCapital: s.paidInShareCapital.parsedValue,
            bonusShareCapital: s.bonusShareCapital.parsedValue,
          }),
        }))
        .filter((s) => s.numberOfShares > 0),
      transactionDate: this.transactionDateCardModel.combinedDateAndTime ?? "",
      nominalValue: this.nominalValue.toNumberOrZero(),
    };
  }

  checkReportingYearFor(date: string) {
    const currentYear = new Date().getFullYear();
    const transactionYear = toDate(date).getFullYear();
    if (transactionYear === currentYear && isNorwegianOrgNo(this.orgNo)) {
      this.shareholdingPerDateUnsupportedForARError = {
        key: "transactions.shareholdingPerDate.transactionDate.info",
        params: { year: currentYear.toString(), latest: `31.12.${currentYear - 1}` },
      };
    } else {
      this.shareholdingPerDateUnsupportedForARError = undefined;
    }
  }

  toggleEnterShareNumbers() {
    this.enterShareNumbers = !this.enterShareNumbers;
  }

  toggleEnterTaxPositions() {
    this.enterTaxPositions = !this.enterTaxPositions;
  }

  get excludedShareholders() {
    return excludedShareholdersFromActors(this.formShareholders);
  }

  addNewSubscribers(shareholders: ExtendedShareholderDTO[], onSuccess: () => void) {
    excludeExistingShareholders(
      shareholders,
      this.formShareholders.map((shareholder) => shareholder.id),
    ).forEach((shareholder) => this.addNewShareholder(shareholder));
    onSuccess();
  }

  clearDirty() {
    this.isDirty = false;
  }
}
